import { fmtDateOnly } from 'utils/userTimezone';
import React from 'react';
import { Conversation, ChatType } from 'utils/chatTypes';
import { composeDisplayName } from 'utils/displayName';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, User, Building, MessageSquare, MessageSquareText, Trash2, Search, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useCurrentUser } from 'app';
import { AvatarStack } from './AvatarStack';
import { useState, useEffect, useMemo } from 'react';
import { getFirestore, getDocs, query, collection, where } from 'firebase/firestore';
import { firebaseApp, firestore } from 'app';
import { useProfileStore } from 'utils/useProfileStore';
import { deleteConversation, conversationHasUnread } from 'utils/chatService';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Props {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  onSelectConversation: (conversation: Conversation) => void;
  isLoading: boolean;
  isMobile?: boolean;
}

export const ConversationList: React.FC<Props> = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  isLoading,
  isMobile = false
}) => {
  const { user } = useCurrentUser();
  const { profile } = useProfileStore();
  const [participantDetails, setParticipantDetails] = useState<{[userId: string]: {name: string, photoURL?: string, role?: string}}>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<Conversation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const isAdmin = profile?.role === 'admin';

  // Load participant details for all conversations
  useEffect(() => {
    const loadAllParticipantDetails = async () => {
      const allUserIds = new Set<string>();
      
      // Collect all unique user IDs from all conversations
      conversations.forEach(conv => {
        conv.participants.forEach(userId => {
          if (userId !== user?.uid) {
            allUserIds.add(userId);
          }
        });
      });
      
      if (allUserIds.size === 0) return;
      
      const details: {[userId: string]: {name: string, photoURL?: string, role?: string}} = {};
      
      try {
        const db = firestore;
        
        // Fetch details for all users
        const userPromises = Array.from(allUserIds).map(async (userId) => {
          try {
            const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              details[userId] = {
                name: composeDisplayName(userData),
                photoURL: userData.photoURL,
                role: userData.role
              };
            } else {
              details[userId] = { 
                name: 'Unknown User',
                photoURL: undefined,
                role: undefined
              };
            }
          } catch (error) {
            console.error(`Error loading details for user ${userId}:`, error);
            details[userId] = { 
              name: 'Unknown User',
              photoURL: undefined,
              role: undefined
            };
          }
        });
        
        await Promise.all(userPromises);
        setParticipantDetails(details);
      } catch (error) {
        console.error('Error loading participant details:', error);
      }
    };
    
    if (conversations.length > 0) {
      loadAllParticipantDetails();
    }
  }, [conversations, user?.uid]);

  // Helper function to get conversation name (moved before useMemo)
  const getConversationName = (conversation: Conversation) => {
    // Guard against null/undefined conversation
    if (!conversation || !conversation.participants) {
      return 'Chat';
    }
    
    if (conversation.name) return conversation.name;
    
    // Helper to format participant names
    const getParticipantNames = () => {
      const otherParticipants = conversation.participants
        .filter(userId => userId !== user?.uid) // Exclude current user
        .map(userId => {
          const participant = participantDetails[userId];
          if (!participant) return null;
          
          const fullName = participant.name || 'Unknown';
          const nameParts = fullName.split(' ');
          
          if (nameParts.length >= 2) {
            const firstName = nameParts[0];
            const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
            return `${firstName} ${lastInitial}`;
          }
          
          return fullName; // Fallback if only one name part
        })
        .filter(name => name !== null);
      
      if (otherParticipants.length === 0) return null;
      if (otherParticipants.length === 1) return otherParticipants[0];
      if (otherParticipants.length === 2) return `${otherParticipants[0]} & ${otherParticipants[1]}`;
      
      // For 3+ people: "Name1, Name2 & Name3"
      const lastPerson = otherParticipants[otherParticipants.length - 1];
      const otherPeople = otherParticipants.slice(0, -1).join(', ');
      return `${otherPeople} & ${lastPerson}`;
    };
    
    switch (conversation.type) {
      case 'one_on_one':
        // Try to get the other user's name from participant details first
        const otherUserId = conversation.participants.find(id => id !== user?.uid);
        if (otherUserId && participantDetails[otherUserId]) {
          return participantDetails[otherUserId].name;
        }
        return conversation.otherUserName || getParticipantNames() || 'Direct Message';
      case 'group':
        // Format as "FirstName LastInitial" for each staff member
        const staffNames = conversation.participants
          .filter(userId => userId !== user?.uid) // Exclude current user
          .map(userId => {
            const participant = participantDetails[userId];
            if (!participant) return null;
            
            const fullName = participant.name || 'Unknown';
            const nameParts = fullName.split(' ');
            
            if (nameParts.length >= 2) {
              const firstName = nameParts[0];
              const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
              return `${firstName} ${lastInitial}`;
            }
            
            return fullName; // Fallback if only one name part
          })
          .filter(name => name !== null);
        
        if (staffNames.length === 0) return 'Group Chat';
        if (staffNames.length === 1) return staffNames[0];
        if (staffNames.length === 2) return `${staffNames[0]} & ${staffNames[1]}`;
        
        // For 3+ people: "Name1, Name2 & Name3"
        const lastPerson = staffNames[staffNames.length - 1];
        const otherPeople = staffNames.slice(0, -1).join(', ');
        return `${otherPeople} & ${lastPerson}`;
        
      case 'client_discussion':
        return conversation.clientName ? `Discussion about ${conversation.clientName}` : 'Client Discussion';
      case 'admin_client':
        // For admin_client conversations, find the client participant
        const clientParticipant = conversation.participants.find(userId => {
          if (userId === user?.uid) return false; // Not the current user
          const participant = participantDetails[userId];
          return participant && (participant.role === 'client' || participant.role === 'limited_access');
        });
        
        if (clientParticipant && participantDetails[clientParticipant]) {
          return participantDetails[clientParticipant].name;
        }
        
        // Fallback: show other participant names
        return getParticipantNames() || 'Client Chat';
      default:
        // For any other type, show the other participants' names
        return getParticipantNames() || 'Chat';
    }
  };

  // Comprehensive search filter function
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) {
      return conversations;
    }
    
    const query = searchQuery.toLowerCase().trim();
    
    return conversations.filter(conversation => {
      // 1. Search by conversation name
      const conversationName = getConversationName(conversation).toLowerCase();
      if (conversationName.includes(query)) {
        return true;
      }
      
      // 2. Search by participant names
      for (const participantId of conversation.participants) {
        if (participantId === user?.uid) continue; // Skip current user
        
        const participant = participantDetails[participantId];
        if (participant?.name && participant.name.toLowerCase().includes(query)) {
          return true;
        }
      }
      
      // 3. Search by client name for client_discussion type
      if (conversation.type === 'client_discussion' && conversation.clientName) {
        if (conversation.clientName.toLowerCase().includes(query)) {
          return true;
        }
      }
      
      // 4. Search by other user name for one_on_one type
      if (conversation.type === 'one_on_one' && conversation.otherUserName) {
        if (conversation.otherUserName.toLowerCase().includes(query)) {
          return true;
        }
      }
      
      return false;
    });
  }, [conversations, searchQuery, participantDetails, user?.uid]);
  
  const getConversationIcon = (type: ChatType) => {
    switch (type) {
      case 'one_on_one':
        return <MessageSquare className="h-4 w-4" />;
      case 'group':
        return <Users className="h-4 w-4" />;
      case 'client_discussion':
        return <MessageSquareText className="h-4 w-4" />;
      case 'admin_client':
        return <User className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: ChatType) => {
    switch (type) {
      case 'one_on_one':
        return 'bg-teal-100 text-teal-800';
      case 'group':
        return 'bg-green-100 text-green-800';
      case 'client_discussion':
        return 'bg-purple-100 text-purple-800';
      case 'admin_client':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimeForMobile = (date: Date) => {
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return `${minutes}m`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h`;
    } else {
      return fmtDateOnly(date, 'MMM d');
    }
  };

  // Helper function to check if conversation has unread messages for current user
  const hasUnreadMessages = (conversation: Conversation, currentUserId: string) =>
    conversationHasUnread(conversation, currentUserId);

  const getConversationAvatar = (conversation: Conversation) => {
    if (conversation.type === 'admin_client') {
      // For admin_client conversations, find the client participant's avatar
      const clientParticipant = conversation.participants.find(userId => {
        if (userId === user?.uid) return false; // Not the current user
        const participant = participantDetails[userId];
        return participant && (participant.role === 'client' || participant.role === 'limited_access');
      });
      
      if (clientParticipant && participantDetails[clientParticipant]) {
        return participantDetails[clientParticipant].photoURL;
      }
      
      // Fallback: if no role info yet, try to find non-staff participant
      const potentialClient = conversation.participants.find(userId => {
        if (userId === user?.uid) return false; // Not the current user
        const participant = participantDetails[userId];
        // If we have role info and it's staff/admin, exclude it
        if (participant && participant.role && (participant.role === 'admin' || participant.role === 'staff')) {
          return false;
        }
        return true; // Likely a client
      });
      
      if (potentialClient && participantDetails[potentialClient]) {
        return participantDetails[potentialClient].photoURL;
      }
    }
    
    return conversation.otherUserAvatar;
  };

  const handleDeleteConversation = async (conversation: Conversation) => {
    setDeleteDialogOpen(true);
    setConversationToDelete(conversation);
  };

  const confirmDelete = async () => {
    if (!conversationToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteConversation(conversationToDelete.id);
      toast.success('Conversation deleted successfully');
    } catch (error: any) {
      console.error('Error deleting conversation:', error);
      
      // Show specific error message if available
      const errorMessage = error.message || 'Failed to delete conversation';
      
      if (errorMessage.includes('Insufficient permissions')) {
        toast.error('You need admin or staff privileges to delete conversations. Please contact an administrator.');
      } else if (errorMessage.includes('permission-denied')) {
        toast.error('Permission denied. Please refresh your token if you recently received admin privileges.');
      } else {
        toast.error(errorMessage);
      }
    }
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
    setIsDeleting(false);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
  };

  if (isLoading) {
    return (
      <div className={isMobile ? "p-3" : "p-4"}>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-3">
                <div className={`${isMobile ? 'h-12 w-12' : 'h-10 w-10'} bg-gray-200 rounded-full`} />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className={`${isMobile ? 'p-6' : 'p-4'} text-center text-gray-500`}>
        <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className={isMobile ? 'text-sm' : 'text-base'}>No conversations yet</p>
        <p className={`text-sm ${isMobile ? 'mt-2' : ''}`}>Start a new chat to get started</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search Input - Fixed at top */}
      <div className="flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search conversations by name or participant..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 h-10"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 text-gray-400 hover:text-gray-600"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Conversations List - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* No search results state */}
        {searchQuery && filteredConversations.length === 0 ? (
          <div className={`${isMobile ? 'p-6' : 'p-4'} text-center text-gray-500`}>
            <Search className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className={isMobile ? 'text-sm font-medium' : 'text-base font-medium'}>No conversations found</p>
            <p className={`text-sm ${isMobile ? 'mt-2' : 'mt-1'}`}>No conversations match "{searchQuery}"</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchQuery('')}
              className="mt-3 text-teal-600 hover:text-teal-700"
            >
              Clear search
            </Button>
          </div>
        ) : (
          filteredConversations.map((conversation) => {
            const isSelected = selectedConversation?.id === conversation.id;
            
            return (
              <div
                key={conversation.id}
                className={`${
                  isMobile 
                    ? 'p-4 border-b border-gray-100 cursor-pointer active:bg-gray-50 transition-colors'
                    : `p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors group ${
                        isSelected ? 'bg-teal-50 border-teal-200' : ''
                      }`
                }`}
                onClick={() => onSelectConversation(conversation)}
              >
                <div className="flex items-start space-x-3">
                  <div className="relative">
                    {/* Show different avatar types based on conversation type */}
                    {conversation.type === 'group' ? (
                      <AvatarStack 
                        {...({
                          participants: conversation.participants,
                          currentUserId: user?.uid || '',
                          size: isMobile ? 'lg' : 'md',
                          maxVisible: 3,
                        } as any)}
                      />
                    ) : (
                      <Avatar className={isMobile ? "h-12 w-12" : "h-10 w-10"}>
                        <AvatarImage src={getConversationAvatar(conversation)} alt={getConversationName(conversation)} />
                        <AvatarFallback className={getTypeColor(conversation.type)}>
                          {getConversationIcon(conversation.type)}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    {/* Online indicator for mobile one-on-one chats */}
                    {isMobile && conversation.type === 'one_on_one' && (
                      <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 bg-green-500 border-2 border-white rounded-full" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`${
                        isMobile 
                          ? 'text-base font-semibold text-gray-900 truncate'
                          : 'text-sm font-medium text-gray-900 truncate'
                      }`}>
                        {getConversationName(conversation)}
                      </h3>
                      {conversation.lastMessageAt && (
                        <span className={`${
                          isMobile 
                            ? 'text-sm text-gray-500 font-medium'
                            : 'text-xs text-gray-500'
                        }`}>
                          {isMobile 
                            ? formatTimeForMobile(conversation.lastMessageAt)
                            : formatDistanceToNow(conversation.lastMessageAt, { addSuffix: true })
                          }
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <p className={`${
                        isMobile 
                          ? 'text-sm text-gray-600 truncate leading-relaxed'
                          : 'text-sm text-gray-500 truncate'
                      }`}>
                        {conversation.lastMessage || 'No messages yet'}
                      </p>
                      {/* Unread indicator for mobile */}
                      {isMobile && hasUnreadMessages(conversation, user?.uid || '') && (
                        <div className="ml-2 h-2 w-2 bg-teal-500 rounded-full flex-shrink-0" />
                      )}
                    </div>
                    
                    {conversation.type === 'client_discussion' && conversation.clientName && (
                      <p className={`${
                        isMobile 
                          ? 'text-xs text-purple-600 mt-2 font-medium'
                          : 'text-xs text-purple-600 mt-1'
                      }`}>
                        Client: {conversation.clientName}
                      </p>
                    )}
                  </div>
                  
                  {/* Right side - unread indicator and delete button */}
                  <div className="flex flex-col items-end justify-between h-full mt-1">
                    {/* Unread indicator for desktop */}
                    {!isMobile && hasUnreadMessages(conversation, user?.uid || '') && (
                      <div className="h-2 w-2 bg-teal-500 rounded-full mt-1" />
                    )}
                    
                    <div className="flex flex-col items-end gap-2 mt-auto pt-[10px]">
                      {/* Delete button for admin users */}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 opacity-100 transition-opacity hover:bg-red-50 hover:text-red-600 bg-red-100 text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteConversation(conversation);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this conversation with "{getConversationName(conversationToDelete || {} as Conversation)}"? 
              This action cannot be undone and will permanently delete all messages in this conversation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete} disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
